/**
 * Tours run outside React. ChatSidebarProvider registers note ensure/upsert
 * so driver.js popover controls can write FOC feedback notes.
 */

import { FOC_TOUR_FEEDBACK_ATTR } from './focTourFeedback';
import {
  FOC_STEP_REQ_KEY_ATTR,
  FOC_STEP_REACTION_ATTR,
  FOC_STEP_TEXT_ATTR,
  FOC_STEP_TOUR_LABEL_ATTR,
} from './focStepFeedbackPopover';
import {
  getFocStepFeedback,
  setFocStepFeedback,
} from './focStepFeedbackSession';

export type OpenNotesFeedbackFn = (tourLabel: string) => Promise<void>;

export type EnsureFocFeedbackNoteFn = (tourLabel: string) => Promise<string | null>;

export type UpsertFocStepFeedbackFn = (input: {
  tourLabel: string;
  requirementKey: string;
  reaction: string;
  text: string;
}) => Promise<void>;

let openNotesFeedbackImpl: OpenNotesFeedbackFn | null = null;
let ensureFocFeedbackNoteImpl: EnsureFocFeedbackNoteFn | null = null;
let upsertFocStepFeedbackImpl: UpsertFocStepFeedbackFn | null = null;

export function registerOpenNotesFeedback(
  fn: OpenNotesFeedbackFn | null
): () => void {
  openNotesFeedbackImpl = fn;
  return () => {
    if (openNotesFeedbackImpl === fn) {
      openNotesFeedbackImpl = null;
    }
  };
}

export function registerEnsureFocFeedbackNote(
  fn: EnsureFocFeedbackNoteFn | null
): () => void {
  ensureFocFeedbackNoteImpl = fn;
  return () => {
    if (ensureFocFeedbackNoteImpl === fn) {
      ensureFocFeedbackNoteImpl = null;
    }
  };
}

export function registerUpsertFocStepFeedback(
  fn: UpsertFocStepFeedbackFn | null
): () => void {
  upsertFocStepFeedbackImpl = fn;
  return () => {
    if (upsertFocStepFeedbackImpl === fn) {
      upsertFocStepFeedbackImpl = null;
    }
  };
}

export async function openNotesFeedback(tourLabel: string): Promise<void> {
  if (!openNotesFeedbackImpl) {
    console.warn(
      '[focTourFeedback] openNotesFeedback is not registered (ChatSidebarProvider not mounted).'
    );
    return;
  }
  await openNotesFeedbackImpl(tourLabel);
}

export async function ensureFocFeedbackNote(
  tourLabel: string
): Promise<string | null> {
  if (!ensureFocFeedbackNoteImpl) {
    console.warn(
      '[focTourFeedback] ensureFocFeedbackNote is not registered (ChatSidebarProvider not mounted).'
    );
    return null;
  }
  return ensureFocFeedbackNoteImpl(tourLabel);
}

export async function upsertFocStepFeedback(input: {
  tourLabel: string;
  requirementKey: string;
  reaction: string;
  text: string;
}): Promise<void> {
  setFocStepFeedback(input.tourLabel, input.requirementKey, {
    reaction: input.reaction,
    text: input.text,
  });
  if (!upsertFocStepFeedbackImpl) {
    console.warn(
      '[focTourFeedback] upsertFocStepFeedback is not registered (ChatSidebarProvider not mounted).'
    );
    return;
  }
  await upsertFocStepFeedbackImpl(input);
}

let pendingTextTimer: ReturnType<typeof setTimeout> | null = null;
let pendingTextPayload: {
  tourLabel: string;
  requirementKey: string;
  text: string;
} | null = null;

async function persistStepFeedback(
  tourLabel: string,
  requirementKey: string,
  patch: Partial<{ reaction: string; text: string }>
): Promise<void> {
  const current = getFocStepFeedback(tourLabel, requirementKey);
  const next = {
    reaction: patch.reaction ?? current.reaction,
    text: patch.text ?? current.text,
  };
  await upsertFocStepFeedback({
    tourLabel,
    requirementKey,
    reaction: next.reaction,
    text: next.text,
  });
}

/** Flush any debounced textarea value before Next/Done. */
export async function flushFocStepFeedback(): Promise<void> {
  if (pendingTextTimer) {
    clearTimeout(pendingTextTimer);
    pendingTextTimer = null;
  }
  const pending = pendingTextPayload;
  pendingTextPayload = null;
  if (!pending) return;
  await persistStepFeedback(pending.tourLabel, pending.requirementKey, {
    text: pending.text,
  });
}

/**
 * Wire driver.js popover links (final-step note open) and per-step Feedback
 * emoji / textarea controls.
 */
export function bindFocTourFeedbackLinkClicks(
  root: ParentNode = document
): () => void {
  const onClick = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const reactionBtn = target.closest(`button[${FOC_STEP_REACTION_ATTR}]`);
    if (reactionBtn instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      const tourLabel = reactionBtn
        .getAttribute(FOC_STEP_TOUR_LABEL_ATTR)
        ?.trim();
      const requirementKey = reactionBtn
        .getAttribute(FOC_STEP_REQ_KEY_ATTR)
        ?.trim();
      const reaction = reactionBtn.getAttribute(FOC_STEP_REACTION_ATTR) ?? '';
      if (!tourLabel || !requirementKey) return;
      const current = getFocStepFeedback(tourLabel, requirementKey);
      const nextReaction = current.reaction === reaction ? '' : reaction;
      const section = reactionBtn.closest('.foc-step-feedback');
      section
        ?.querySelectorAll(`button[${FOC_STEP_REACTION_ATTR}]`)
        .forEach((btn) => {
          const selected =
            btn.getAttribute(FOC_STEP_REACTION_ATTR) === nextReaction;
          btn.classList.toggle('foc-step-feedback__btn--selected', selected);
          btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
      void persistStepFeedback(tourLabel, requirementKey, {
        reaction: nextReaction,
      });
      return;
    }

    const anchor = target.closest(`a[${FOC_TOUR_FEEDBACK_ATTR}]`);
    if (!anchor) return;
    event.preventDefault();
    event.stopPropagation();
    const tourLabel = anchor.getAttribute(FOC_TOUR_FEEDBACK_ATTR)?.trim();
    if (!tourLabel) return;
    void flushFocStepFeedback().then(() => openNotesFeedback(tourLabel));
  };

  const onInput = (event: Event) => {
    const target = event.target;
    if (!(target instanceof HTMLTextAreaElement)) return;
    if (!target.hasAttribute(FOC_STEP_TEXT_ATTR)) return;
    const tourLabel = target.getAttribute(FOC_STEP_TOUR_LABEL_ATTR)?.trim();
    const requirementKey = target.getAttribute(FOC_STEP_REQ_KEY_ATTR)?.trim();
    if (!tourLabel || !requirementKey) return;
    const text = target.value;
    setFocStepFeedback(tourLabel, requirementKey, {
      ...getFocStepFeedback(tourLabel, requirementKey),
      text,
    });
    pendingTextPayload = { tourLabel, requirementKey, text };
    if (pendingTextTimer) clearTimeout(pendingTextTimer);
    pendingTextTimer = setTimeout(() => {
      pendingTextTimer = null;
      const pending = pendingTextPayload;
      pendingTextPayload = null;
      if (!pending) return;
      void persistStepFeedback(pending.tourLabel, pending.requirementKey, {
        text: pending.text,
      });
    }, 400);
  };

  root.addEventListener('click', onClick, true);
  root.addEventListener('input', onInput, true);
  return () => {
    root.removeEventListener('click', onClick, true);
    root.removeEventListener('input', onInput, true);
    if (pendingTextTimer) {
      clearTimeout(pendingTextTimer);
      pendingTextTimer = null;
    }
  };
}
